import { useState } from 'react';
import { NoteClient, NoteDTO, ApiResponse_string } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useNoteApi = () => {
    const apiClient = new NoteClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: NoteDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createNote(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Note');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: NoteDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateNote(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Note');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getNoteById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Note');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchNotes(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Notes');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteNote(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Note');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportNotes(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting Notes', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const resolve = async (id: string) => {
        setIsLoading(true);
        try {
            await (apiClient as any).resolveNote(id);
        } catch (err: any) {
            setError(err.message || 'Error resolving Note');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, resolve, isLoading, error };
};